import React from "react";

import { useIntl } from "react-intl";

import Flag, {
    CustomFlagProps
} from "@/components/fastshift/src/contexts/FlagProvider";
import {
    useFastshiftSelector,
    useFastshiftDispatch
} from "@/components/fastshift/src/state";
import * as StateActions from "@/components/fastshift/src/state/actions";
import { captureErrorWithContext } from "@/components/fastshift/src/state/utils";
import { useBxpWebStargateClient } from "@/libraries/bxp-web-stargate-client/src";

import { CONSENT_PAGE_LEGAL_DISPLAY_TEXT_VERSION } from "../constants";
import { messages } from "../messages";

const UNKNOWN_REGION = "UNKNOWN";

export const useSubmitUserConsentWithFlags = () => {
    const stargateClient = useBxpWebStargateClient();
    const intl = useIntl();
    const dispatch = useFastshiftDispatch();
    const [submitIsLoading, setSubmitIsLoading] =
        React.useState<boolean>(false);

    const { data: userDetails } = useFastshiftSelector(
        (state) => state.userDetails
    );

    const { showFlag } = React.useContext(Flag.Context);

    const successFlag: CustomFlagProps = {
        id: "success-flag",
        iconType: "success",
        styleOverride: true,
        autoDismiss: true,
        title: intl.formatMessage(
            messages.legalConsentPortalFeature_SuccessFlag_Title
        ),
        description: intl.formatMessage(
            messages.legalConsentPortalFeature_SuccessFlag_Description
        )
    };

    const errorFlag: CustomFlagProps = {
        id: "error-flag",
        iconType: "error",
        styleOverride: true,
        autoDismiss: true,
        title: intl.formatMessage(
            messages.legalConsentPortalFeature_ErrorFlag_Title
        ),
        description: intl.formatMessage(
            messages.legalConsentPortalFeature_ErrorFlag_Description
        )
    };

    const displayAndReportError = (message: string): void => {
        setSubmitIsLoading(false);
        showFlag(errorFlag);
        captureErrorWithContext({
            error: "Error on fetching user consent state",
            message,
            userDomain: userDetails?.userDomain?.domain
        });
    };

    const submitUserConsentWithFlags = (): void => {
        setSubmitIsLoading(true);
        stargateClient
            .sendFastshiftPortalConsent({
                requestBody: {
                    displayedText: CONSENT_PAGE_LEGAL_DISPLAY_TEXT_VERSION,
                    locale: UNKNOWN_REGION
                }
            })
            .then((response) => {
                if (response.data.consent && response.success) {
                    dispatch(StateActions.setPortalUserConsent(true));
                    setSubmitIsLoading(false);
                    showFlag(successFlag);
                } else {
                    displayAndReportError(
                        `Backend request succeeded, but response data was unexpected: ${JSON.stringify(
                            response
                        )}`
                    );
                }
            })
            .catch((message) => {
                displayAndReportError(message);
            });
    };

    return { submitUserConsentWithFlags, submitIsLoading };
};
